import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import styles from './RichHtmlEditor.module.css';

// Lightweight rich-text/HTML editor for the chat-end email body. A Visual mode
// (contentEditable + a small formatting toolbar) and a Source mode (raw HTML)
// edit the same HTML string. Exposes `insertAtCursor` so an external variable
// palette can drop a token at the caret in whichever mode is active.
//
// Uses document.execCommand for formatting — long deprecated but still the
// simplest cross-browser path for a contentEditable toolbar, and adequate for
// an internal admin tool.

// Strip script/embed elements, inline event handlers, and javascript: URLs
// before rendering stored HTML into the contentEditable. The content is
// super-admin-authored (and only a super-admin renders it), so this is
// defense-in-depth against a pasted footgun rather than a cross-user vector.
// Parsing into a <template> is inert — no scripts run, no resources load.
const FORBIDDEN_TAGS = new Set(['script', 'iframe', 'object', 'embed', 'link', 'meta', 'style', 'base']);
export function sanitizeHtml(html: string): string {
  if (typeof document === 'undefined' || !html) return html;
  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  const els: Element[] = [];
  const walker = document.createTreeWalker(tpl.content, NodeFilter.SHOW_ELEMENT);
  while (walker.nextNode()) els.push(walker.currentNode as Element);
  for (const el of els) {
    if (FORBIDDEN_TAGS.has(el.tagName.toLowerCase())) {
      el.remove();
      continue;
    }
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      if (name.startsWith('on')) el.removeAttribute(attr.name);
      else if ((name === 'href' || name === 'src') && /^\s*javascript:/i.test(attr.value)) {
        el.removeAttribute(attr.name);
      }
    }
  }
  return tpl.innerHTML;
}

export interface RichHtmlEditorHandle {
  /** Insert literal text at the current caret (Visual or Source). */
  insertAtCursor: (text: string) => void;
  /** Did focus last land inside this editor? */
  hasFocus: () => boolean;
}

interface Props {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  onFocus?: () => void;
}

export const RichHtmlEditor = forwardRef<RichHtmlEditorHandle, Props>(function RichHtmlEditor(
  { value, onChange, placeholder, onFocus },
  ref,
) {
  const [mode, setMode] = useState<'visual' | 'source'>('visual');
  const editorRef = useRef<HTMLDivElement>(null);
  const sourceRef = useRef<HTMLTextAreaElement>(null);
  const focusedRef = useRef(false);

  // Sync external value into the contentEditable DOM only when it actually
  // differs and the user isn't mid-edit (avoids caret jumps on each keystroke).
  useEffect(() => {
    const el = editorRef.current;
    if (mode === 'visual' && el && document.activeElement !== el) {
      const clean = sanitizeHtml(value);
      if (el.innerHTML !== clean) el.innerHTML = clean;
    }
  }, [value, mode]);

  function emitVisual() {
    onChange(sanitizeHtml(editorRef.current?.innerHTML ?? ''));
  }

  function exec(command: string, arg?: string) {
    editorRef.current?.focus();
    document.execCommand(command, false, arg);
    emitVisual();
  }

  useImperativeHandle(ref, () => ({
    hasFocus: () => focusedRef.current,
    insertAtCursor: (text: string) => {
      if (mode === 'source') {
        const ta = sourceRef.current;
        if (!ta) return;
        const start = ta.selectionStart ?? value.length;
        const end = ta.selectionEnd ?? start;
        const next = value.slice(0, start) + text + value.slice(end);
        onChange(next);
        requestAnimationFrame(() => {
          ta.focus();
          const pos = start + text.length;
          ta.setSelectionRange(pos, pos);
        });
        return;
      }
      const el = editorRef.current;
      if (!el) return;
      el.focus();
      // If the caret isn't inside the editor, drop it at the end.
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0 || !el.contains(sel.anchorNode)) {
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
      document.execCommand('insertText', false, text);
      emitVisual();
    },
  }));

  return (
    <div className={styles.wrap}>
      <div className={styles.toolbar}>
        {mode === 'visual' && (
          <>
            <button type="button" className={styles.tbtn} title="Bold" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('bold')}><b>B</b></button>
            <button type="button" className={styles.tbtn} title="Italic" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('italic')}><i>I</i></button>
            <button type="button" className={styles.tbtn} title="Underline" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('underline')}><u>U</u></button>
            <button type="button" className={styles.tbtn} title="Heading" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('formatBlock', 'h2')}>H</button>
            <button type="button" className={styles.tbtn} title="Paragraph" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('formatBlock', 'p')}>¶</button>
            <button type="button" className={styles.tbtn} title="Bulleted list" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('insertUnorderedList')}>•</button>
            <button type="button" className={styles.tbtn} title="Numbered list" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('insertOrderedList')}>1.</button>
            <button type="button" className={styles.tbtn} title="Link" onMouseDown={(e) => e.preventDefault()} onClick={() => { const url = window.prompt('Link URL'); if (url) exec('createLink', url); }}>🔗</button>
            <button type="button" className={styles.tbtn} title="Clear formatting" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('removeFormat')}>⨯</button>
          </>
        )}
        <span className={styles.spacer} />
        <button
          type="button"
          className={styles.modeToggle}
          onClick={() => setMode((m) => (m === 'visual' ? 'source' : 'visual'))}
        >
          {mode === 'visual' ? '< > Source' : 'Visual'}
        </button>
      </div>

      {mode === 'visual' ? (
        <div
          ref={editorRef}
          className={styles.editor}
          contentEditable
          suppressContentEditableWarning
          data-placeholder={placeholder}
          onInput={emitVisual}
          onFocus={() => { focusedRef.current = true; onFocus?.(); }}
          onBlur={() => { focusedRef.current = false; }}
        />
      ) : (
        <textarea
          ref={sourceRef}
          className={styles.source}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => { focusedRef.current = true; onFocus?.(); }}
          onBlur={() => { focusedRef.current = false; }}
        />
      )}
    </div>
  );
});
